import { useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';

import { initAmplitude, setAmplitudeDeviceId } from 'services/amplitude';

/**
 * Applies the `ajs_aid` query param (see docs/adr/010-product-analytics.md)
 * as the Amplitude Device ID, then strips it from the URL once
 * `initAmplitude()` resolves.
 *
 * `router` is a stable reference for the lifetime of the app, so the effect
 * only needs to depend on it and `enabled` — the current `ajs_aid` is read
 * from `router.state` at the time the effect runs, not captured as a
 * dependency. Depending on the value directly would re-fire this effect right
 * after the strip sets it to `undefined`, re-applying/re-initializing a
 * second time.
 */
export function useAmplitudeDeviceIdOverride(enabled: boolean): void {
  const router = useRouter();

  useEffect(() => {
    if (!enabled) return;

    const { ajs_aid: ajsAid } = router.state.location.search as {
      ajs_aid?: string;
    };
    setAmplitudeDeviceId(ajsAid);

    const result = initAmplitude();

    if (!ajsAid) return;

    result?.promise.then(() => {
      router.navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          ajs_aid: undefined,
        }),
        replace: true,
      });
    });
  }, [enabled, router]);
}
